import { useMemo } from 'react'
import { ScreamingSnakeCase } from 'type-fest'
import { config } from '../../../config'
import { Country } from '../../../constants'
import { useCountry } from '../../useCountry'

type Region = 'EU' | 'NA'

type Service = 'appointment' | 'consultant' | 'customer' | 'customerProfile' | 'providerRecommendation' | 'opportunity' | 'partner' | 'user'

type SupportedCountry = Extract<Country, 'CH' | 'DE' | 'US'>
type HostConfig = Partial<Record<SupportedCountry, URL>> & { FALLBACK: URL }

type Params = {
  country?: string
  service: Service
}

const parseServiceUrl = (url: string | undefined, envKey: `REACT_APP_${keyof typeof config}`): URL => {
  if (!url || url.trim() === '') {
    throw new Error(`Invalid or missing service URL for ${envKey}. Add ${envKey} to your .env (see .env.example).`)
  }
  try {
    return new URL(url)
  } catch {
    throw new Error(`Invalid URL for ${envKey}: "${url}". Check your .env (see .env.example).`)
  }
}

// eslint-disable-next-line prettier/prettier
const serviceToRegionUrl: Partial<
  Record<`${ScreamingSnakeCase<Service>}_${Region}`, URL>
> &
  Record<`${ScreamingSnakeCase<Service>}_NA`, URL> = {
  APPOINTMENT_EU: parseServiceUrl(config.APPOINTMENT_SERVICE_URL_EU, 'REACT_APP_APPOINTMENT_SERVICE_URL_EU'),
  APPOINTMENT_NA: parseServiceUrl(config.APPOINTMENT_SERVICE_URL_NA, 'REACT_APP_APPOINTMENT_SERVICE_URL_NA'),
  CONSULTANT_EU: parseServiceUrl(config.CONSULTANT_SERVICE_URL_EU, 'REACT_APP_CONSULTANT_SERVICE_URL_EU'),
  CONSULTANT_NA: parseServiceUrl(config.CONSULTANT_SERVICE_URL_NA, 'REACT_APP_CONSULTANT_SERVICE_URL_NA'),
  CUSTOMER_EU: parseServiceUrl(config.CUSTOMER_SERVICE_URL_EU, 'REACT_APP_CUSTOMER_SERVICE_URL_EU'),
  CUSTOMER_NA: parseServiceUrl(config.CUSTOMER_SERVICE_URL_NA, 'REACT_APP_CUSTOMER_SERVICE_URL_NA'),
  CUSTOMER_PROFILE_EU: parseServiceUrl(config.CUSTOMER_PROFILE_SERVICE_URL_EU, 'REACT_APP_CUSTOMER_PROFILE_SERVICE_URL_EU'),
  CUSTOMER_PROFILE_NA: parseServiceUrl(config.CUSTOMER_PROFILE_SERVICE_URL_NA, 'REACT_APP_CUSTOMER_PROFILE_SERVICE_URL_NA'),
  OPPORTUNITY_EU: parseServiceUrl(config.OPPORTUNITY_SERVICE_URL_EU, 'REACT_APP_OPPORTUNITY_SERVICE_URL_EU'),
  OPPORTUNITY_NA: parseServiceUrl(config.OPPORTUNITY_SERVICE_URL_NA, 'REACT_APP_OPPORTUNITY_SERVICE_URL_NA'),
  PARTNER_EU: parseServiceUrl(config.PARTNER_SERVICE_URL_EU, 'REACT_APP_PARTNER_SERVICE_URL_EU'),
  PARTNER_NA: parseServiceUrl(config.PARTNER_SERVICE_URL_NA, 'REACT_APP_PARTNER_SERVICE_URL_NA'),
  PROVIDER_RECOMMENDATION_EU: parseServiceUrl(
    config.PROVIDER_RECOMMENDATION_SERVICE_URL_EU,
    'REACT_APP_PROVIDER_RECOMMENDATION_SERVICE_URL_EU'
  ),
  PROVIDER_RECOMMENDATION_NA: parseServiceUrl(
    config.PROVIDER_RECOMMENDATION_SERVICE_URL_NA,
    'REACT_APP_PROVIDER_RECOMMENDATION_SERVICE_URL_NA'
  ),
  USER_EU: parseServiceUrl(config.USER_SERVICE_URL_EU, 'REACT_APP_USER_SERVICE_URL_EU'),
  USER_NA: parseServiceUrl(config.USER_SERVICE_URL_NA, 'REACT_APP_USER_SERVICE_URL_NA'),
} as const

const serviceToCountryHostMap: Record<Service, HostConfig> = {
  appointment: {
    CH: serviceToRegionUrl.APPOINTMENT_EU,
    DE: serviceToRegionUrl.APPOINTMENT_EU,
    FALLBACK: serviceToRegionUrl.APPOINTMENT_NA,
  },
  consultant: {
    CH: serviceToRegionUrl.CONSULTANT_EU,
    DE: serviceToRegionUrl.CONSULTANT_EU,
    FALLBACK: serviceToRegionUrl.CONSULTANT_NA,
  },
  customer: {
    CH: serviceToRegionUrl.CUSTOMER_EU,
    DE: serviceToRegionUrl.CUSTOMER_EU,
    FALLBACK: serviceToRegionUrl.CUSTOMER_NA,
  },
  customerProfile: {
    CH: serviceToRegionUrl.CUSTOMER_PROFILE_EU,
    DE: serviceToRegionUrl.CUSTOMER_PROFILE_EU,
    FALLBACK: serviceToRegionUrl.CUSTOMER_PROFILE_NA,
  },
  opportunity: {
    CH: serviceToRegionUrl.OPPORTUNITY_EU,
    DE: serviceToRegionUrl.OPPORTUNITY_EU,
    FALLBACK: serviceToRegionUrl.OPPORTUNITY_NA,
  },
  partner: {
    CH: serviceToRegionUrl.PARTNER_EU,
    DE: serviceToRegionUrl.PARTNER_EU,
    FALLBACK: serviceToRegionUrl.PARTNER_NA,
  },
  providerRecommendation: {
    CH: serviceToRegionUrl.PROVIDER_RECOMMENDATION_EU,
    DE: serviceToRegionUrl.PROVIDER_RECOMMENDATION_EU,
    FALLBACK: serviceToRegionUrl.PROVIDER_RECOMMENDATION_NA,
  },
  user: {
    CH: serviceToRegionUrl.USER_EU,
    DE: serviceToRegionUrl.USER_EU,
    FALLBACK: serviceToRegionUrl.USER_NA,
  },
}

const getServiceCountryUrl = (options: Params) => {
  const { service, country } = options
  const serviceCountryHost = serviceToCountryHostMap[service]

  if (!serviceCountryHost) {
    throw new Error(`Unsupported service: ${service}`)
  }

  const { FALLBACK } = serviceCountryHost

  if (country) {
    return serviceCountryHost[country.toUpperCase() as SupportedCountry] ?? FALLBACK
  }

  return FALLBACK
}

const useServiceCountryUrl = (service: Service) => {
  const country = useCountry()
  return useMemo(() => ({ url: getServiceCountryUrl({ country, service }) }), [country, service])
}

export { useServiceCountryUrl }
